import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { useSearchContext } from '../Context';
import { useQuery } from '@tanstack/react-query';
import { getCategoryList } from '../api';
import { Select, Input } from 'antd';
import { flattenDeep, forEach, get, map, intersection } from 'lodash-es';
import { getTranslate } from '../utils/trans';
import { Category } from '@knowledge/common/components/ItemSelectV5/Index';

export const CategorySelect = memo(() => {
  const { t } = useTranslate();
  const { categoryUniqueId, scene, knowledgeId, includeSpuClassId, setParams } = useSearchContext();
  const { data, isLoading } = useQuery({
    queryKey: ['getCategory', scene, knowledgeId, includeSpuClassId],
    queryFn: () => {
      return getCategoryList({
        scene,
        knowledgeId,
        includeSpuClassId,
      }).then((res) => {
        const list = get(res, 'categoryList', [] as Category[]);
        const needToTranslate = flattenDeep(map(list, (item) => [item.namePath]));
        return Promise.all(
          map(needToTranslate, (item) => {
            return getTranslate(item);
          }),
        )
          .then((res) => {
            let i = 0;
            forEach(list, (item) => {
              forEach(item.namePath, (_, index) => {
                item.namePath[index] = res[i++];
              });
            });
          })
          .then(() => list);
      });
    },
    // staleTime: 5 * 60 * 1000,
    refetchOnMount: true,
  });
  const [searchValue, setSearchValue] = useState('');
  const options = useMemo(() => {
    const res = map(data, (item) => {
      return {
        value: item.categoryUniqueId,
        label: `${item.platform}-${item.namePath.join('-')}(${item.count})`,
      };
    });
    if (searchValue) {
      return res.filter((item) => item.label.includes(searchValue));
    } else {
      return res;
    }
  }, [data, searchValue]);

  useEffect(() => {
    if (!!data?.length && !!categoryUniqueId.length) {
      const dataIds = map(data, 'categoryUniqueId');
      const union = intersection(dataIds, categoryUniqueId);
      if (union.length !== categoryUniqueId.length) {
        setParams({ categoryUniqueId: union });
      }
    }
  }, [data, categoryUniqueId, setParams]);

  return (
    <Select
      mode="multiple"
      loading={isLoading}
      placeholder={t('请选择类目')}
      value={categoryUniqueId}
      onChange={(value) => {
        setParams({ categoryUniqueId: value });
      }}
      showSearch={false}
      dropdownMatchSelectWidth={false}
      dropdownRender={(menu) => {
        return (
          <>
            <div className="p-[8px]">
              <Input
                allowClear
                placeholder={t('department.搜索')}
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                }}
              />
            </div>
            {menu}
          </>
        );
      }}
      onDropdownVisibleChange={() => {
        setSearchValue('');
      }}
      options={options}
      maxTagCount="responsive"
      maxTagTextLength={5}
      className="w-[170px]"
      allowClear
      onClear={() => {
        setParams({ categoryUniqueId: [] });
      }}
    />
  );
});

CategorySelect.displayName = 'ItemSelectV5CategorySelect';
